import { Stepper } from "@shared/components/Stepper";
import { useFormCompletionTracker } from "@shared/hooks/useFormCompletionTracker";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import React, { useMemo } from "react";
import { CustomerInformationStep } from "./stepComponents/ConsumerInformationStep";
import { RequestDetailsStep } from "./stepComponents/RequestDetailsStep";
import { ReviewPaymentStep } from "./stepComponents/ReviewPaymentStep";

export const CreateDisconnectionRequest: React.FC = () => {
  const remoteUtilityId = useRemoteUtilityId();
  const storageKey = `disconnection-request-progress-${remoteUtilityId}`;

  const { startTracking, logTimeUntilStep, stopTracking } =
    useFormCompletionTracker("Disconnection");
  // Memoize the steps array to prevent infinite re-renders
  const steps = useMemo(
    () => [
      {
        title: "Customer Information",
        slug: "customer-info", // Add slug for routing
        component: (
          <CustomerInformationStep
            remoteUtilityId={Number(remoteUtilityId)}
            storageKey={storageKey}
            logTimeUntilStep={logTimeUntilStep} 
          />
        ),
        optional: false,
        infoTitle: "Customer Information Help",
        infoDescription:
          "Enter the customer's account details. You can use the 'Look Up' button to automatically populate fields if you have the account number.",
      },
      {
        title: "Request Details",
        slug: "service-selection", // Add slug for routing
        component: (
          <RequestDetailsStep
            remoteUtilityId={Number(remoteUtilityId)}
            storageKey={storageKey}
            onNext={() => {}}
            onPrevious={() => {}}
            logTimeUntilStep={logTimeUntilStep} 
          />
        ),
        optional: false,
        infoTitle: "Service Selection Help",
        infoDescription:
          "Browse and select the service you need. Use the search bar to quickly find specific services. Each service shows pricing and description to help you choose.",
      },
      {
        title: "Review",
        slug: "review-payment", // Add slug for routing
        component: (
          <ReviewPaymentStep
            remoteUtilityId={Number(remoteUtilityId)}
            storageKey={storageKey}
            logTimeUntilStep={logTimeUntilStep} 
            stopTracking={stopTracking}
          />
        ),
        optional: false,
        infoTitle: "Review & Payment Help",
        infoDescription:
          "Review all your service details and payment information before submitting your request.",
      },
    ],
    []
  ); // Only recreate when requestData or handleStateUpdate changes

  return (
    <div className="flex flex-col gap-5">
      <div>
        <p className="page-title">
          Create Disconnection Request
        </p>
        <p className="page-subtitle">
          Complete the following steps to submit a disconnection request.
        </p>
      </div>

      <Stepper
        steps={steps}
        onStepChange={() => {}}
        onStepSave={async (stepIndex, stepData, isDirty) => {
          // Storage is handled at component level using step helpers
        }}
        enableApiSave={false}
        saveOnStepChange={false}
        trackDirtyState={false}
        autoSave={true}
        persistProgress={true}
        allowSkip={false} // Set to false since all steps are required
        storageKey={storageKey}
        enableRouting={true} // Enable hash-based routing (no 404s on refresh)
        onValidation={undefined} // basePath is not needed for hash-based routing
        onComplete={() => {}}
        showDraftResume={true}
      />
    </div>
  );
};
